//core
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Formik, Form, FormikHelpers, FormikValues } from 'formik';
import { addMinutes, setMinutes, startOfMinute, subMonths, subWeeks } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { Alert, Box, Tooltip, Typography } from "@mui/material";
import Button from "../../../../../UI/Buttons/Button";

//styles
import { useStyles } from "./styles";

//hooks
import { useWebhooksReport } from "../../../../../../store/admin/reports/webhooks_report/useWebhooksReport";
import { usePartners } from "../../../../../../store/admin/partners/usePartners";

//selectors
import {
  selectTriggerPostbackData,
  selectTriggerPostbackError,
  selectTriggerPostbackIsFetching,
  selectPostbackListData,
  selectPostbackListIsFetching,
  selectTrackerListData,
  selectTrackerListIsFetching,
} from "../../../../../../store/admin/reports/webhooks_report/selectors";
import { selectAffiliatesData, selectAffiliatesIsFetching } from "../../../../../../store/admin/partners/selectors";

//utils
import { getDataIds } from "../../../utils";

//validation
import { validationRequiredAutocompleteField } from "../../../../../validation/requiredAutocompleteStringId.validation";
import { validationRequiredAutocompleteArrayField } from "../../../../../validation/requiredAutocompleteArray.validation";

//components
import MuiAutocompleteSelect from "../../../../../UI/FormFields/MuiAutocompleteSelect";
import MuiDateTimeRangePicker from "../../../../../UI/FormFields/MuiDateTimeRangePicker";
import InfoAlert from "../../../../../UI/InfoAlert";

interface ITriggerPostbackProps {
  setToggleModal: (state: boolean) => void;
}

const TriggerPostback: React.FC<ITriggerPostbackProps> = ({ setToggleModal }): JSX.Element => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const formRef: any = useRef();

  const { getAffiliates, clearAffiliates } = usePartners();
  const {
    triggerPostback,
    clearTriggerPostbackState,
    getPostbackList,
    clearPostbackListState,
    getTrackerList,
    clearTrackerListState,
  } = useWebhooksReport();

  const triggerPostbackData = useSelector(selectTriggerPostbackData);
  const triggerPostbackError = useSelector(selectTriggerPostbackError);
  const isFetching = useSelector(selectTriggerPostbackIsFetching);

  const postbackListData = useSelector(selectPostbackListData);
  const postbackListIsFetching = useSelector(selectPostbackListIsFetching);

  const affiliatesData = useSelector(selectAffiliatesData);
  const affiliatesIsFetching = useSelector(selectAffiliatesIsFetching);

  const trackerListData = useSelector(selectTrackerListData);
  const trackerListIsFetching = useSelector(selectTrackerListIsFetching);

  const [errorsMessages, setErrorsMessages] = useState({
    required: t("common.validation.required_field"),
  });

  const minSentFrom = useMemo(() => {
    const date = startOfMinute(addMinutes(subMonths(new Date(), 1), 10));
    return setMinutes(date, Math.ceil(date.getMinutes() / 10) * 10);
  }, []);

  const initialValues: any = {
    affiliateId: null,
    postbackId: null,
    trackerIds: [],
    sentFrom: subWeeks(new Date(), 1),
    sentTo: new Date(),
  };

  useEffect(() => {
    clearAffiliates();

    return () => {
      clearAffiliates();
      clearTrackerListState();
      clearTriggerPostbackState();
      clearPostbackListState();
    };
  }, []);

  useEffect(() => {
    if (!!triggerPostbackData) {
      setToggleModal(false);
    }
  }, [triggerPostbackData]);

  useEffect(() => {
    if (!!triggerPostbackError && triggerPostbackError.errors) {
      const affiliateId = triggerPostbackError.errors['affiliateId'];
      const webhookId = triggerPostbackError.errors['webhookId'];
      const trackerIds = triggerPostbackError.errors['trackerIds'];
      const dateFrom = triggerPostbackError.errors['dateFrom'];
      const dateTo = triggerPostbackError.errors['dateTo'];

      affiliateId && formRef.current.setFieldError('affiliateId', t(affiliateId));
      webhookId && formRef.current.setFieldError('postbackId', t(webhookId));
      trackerIds && formRef.current.setFieldError('trackerIds', t(trackerIds));
      (dateFrom || dateTo) && formRef.current.setFieldError('sentFrom', t(dateFrom || dateTo));
    }
  }, [triggerPostbackError]);

  useEffect(() => {
    setErrorsMessages({
      required: t("common.validation.required_field"),
    });

    setTimeout(() => {
      !!formRef && !!formRef.current && formRef.current.validateForm();
    }, 50)
  }, [i18n.language, t]);

  const validateRequiredSelectField = useCallback(
    (value: any) => validationRequiredAutocompleteField(value, errorsMessages),
    [errorsMessages]
  );
  const validateRequiredArrayField = useCallback(
    (value: any) => validationRequiredAutocompleteArrayField(value, errorsMessages),
    [errorsMessages]
  );

  const submitForm = (values: FormikValues, actions: FormikHelpers<any>): void => {
    if (!!triggerPostbackError) {
      clearTriggerPostbackState();
    }

    const isGlobal = !!values.postbackId.global;

    const payload = {
      affiliateId: values.affiliateId.id,
      webhookId: values.postbackId.id,
      isGlobal,
      ...(!isGlobal ? { trackerIds: getDataIds(values.trackerIds) } : {}),
      dateFrom: formatInTimeZone(new Date(values.sentFrom), "UTC", "yyyy-MM-dd HH:mm:ss"),
      dateTo: formatInTimeZone(new Date(values.sentTo), "UTC", "yyyy-MM-dd HH:mm:ss"),
    };

    triggerPostback(payload);
    actions.setSubmitting(false);
  };

  const handleCancel = () => {
    setToggleModal(false);
  };

  return (
    <>
      <Typography sx={{ mb: 2 }} variant="h6" component="div">
        {t("admin.reports.webhooks_report.triggerPostback.title")}
      </Typography>
      <Typography className={styles.description} variant="body2" color="secondary">
        {t("admin.reports.webhooks_report.triggerPostback.description")}
      </Typography>
      <InfoAlert
        className={styles.info}
        text={t("admin.reports.webhooks_report.triggerPostback.info")}
      />

      <Formik
        validateOnChange
        validateOnBlur
        validateOnMount
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={(values, actions) => submitForm(values, actions)}
      >
        {props => {
          const { values, handleSubmit, isValid, setFieldValue } = props;

          const getAffiliatesOptions = (page: number, search: string | null) => {
            getAffiliates({
              chiefIds: null,
              departmentIds: null,
              platformIds: null,
              status: 'approved',
              page,
              search,
              per_page: 100,
            });
          };

          const getTrackerListOptions = (page: number, search: string | null) => {
            if (!!values.affiliateId && !!values.postbackId) {
              getTrackerList({
                affiliateId: values.affiliateId.id,
                webhookId: values.postbackId.id,
                data: { page, perPage: 100, search },
              });
            }
          };

          const getPostbackListOptions = (
            page: number,
            search: string | null,
          ) => {
            if (!!values.affiliateId) {
              getPostbackList({
                affiliateId: values.affiliateId.id,
                data: { page, search, per_page: 100 },
              });
            }
          };

          const handleAffiliateChange = () => {
            clearPostbackListState();
            clearTrackerListState();
            setFieldValue('postbackId', null, true);
            setFieldValue('trackerIds', [], true);
          };

          const isGlobalPostback = !!values.postbackId && !!values.postbackId.global;

          const handlePostbackIdChange = () => {
            clearTrackerListState();
            setFieldValue('trackerIds', [], true);

            setTimeout(() => {
              !!formRef && !!formRef.current && formRef.current.validateForm();
            }, 50);
          };

          return (
            <Form onSubmit={handleSubmit}>
              <Box className={styles.field}>
                <MuiDateTimeRangePicker
                  nameFrom="sentFrom"
                  nameTo="sentTo"
                  label={t("admin.reports.filter.period")}
                  isRequired={true}
                  minDateTime={minSentFrom}
                  maxDateTime={new Date()}
                />
              </Box>

              <Box className={styles.field}>
                <MuiAutocompleteSelect
                  keyValue="value"
                  keyId="id"
                  id="affiliateId"
                  name="affiliateId"
                  label={t("admin.finance_report.affiliateIds")}
                  value={values.affiliateId}
                  multiple={false}
                  showSelectAll={false}
                  isRequired={true}
                  validate={validateRequiredSelectField}
                  getOptions={getAffiliatesOptions}
                  data={affiliatesData}
                  isFetching={affiliatesIsFetching}
                  labelWithId={false}
                  clearOnBlur={true}
                  onChangeCallback={handleAffiliateChange}
                />
              </Box>

              <Box className={styles.field}>
                <MuiAutocompleteSelect
                  keyValue="name"
                  keyId="id"
                  id="postbackId"
                  name="postbackId"
                  label={t("admin.reports.webhooks_report.triggerPostback.postbackId")}
                  value={values.postbackId}
                  multiple={false}
                  showSelectAll={false}
                  disabled={!values.affiliateId}
                  isRequired={true}
                  validate={validateRequiredSelectField}
                  getOptions={getPostbackListOptions}
                  data={postbackListData}
                  isFetching={postbackListIsFetching}
                  labelWithId={true}
                  clearOnBlur={true}
                  onChangeDeals={handlePostbackIdChange}
                />
              </Box>

              <Tooltip
                title={isGlobalPostback ? t("admin.reports.webhooks_report.triggerPostback.globalTrackersTooltip") : ''}
                placement="top-start"
              >
                <Box className={styles.field}>
                  <MuiAutocompleteSelect
                    keyValue="value"
                    keyId="id"
                    id="trackerIds"
                    name="trackerIds"
                    label={t("common.forms.fields.tracker")}
                    value={values.trackerIds}
                    multiple={true}
                    showSelectAll={false}
                    disabled={!values.postbackId || isGlobalPostback}
                    isRequired={!isGlobalPostback}
                    validate={isGlobalPostback ? undefined : validateRequiredArrayField}
                    getOptions={getTrackerListOptions}
                    data={trackerListData}
                    isFetching={trackerListIsFetching}
                    labelWithId={false}
                    clearOnBlur={true}
                  />
                </Box>
              </Tooltip>

              {!!triggerPostbackError && !!triggerPostbackError?.error && (
                <Alert sx={{ mb: 2 }} variant="outlined" severity="error">
                  {t(`${triggerPostbackError.error}`)}
                </Alert>
              )}

              <Box className={styles.actions}>
                <Button
                  disableElevation
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={!isValid || isFetching}
                >
                  {t("common.buttons.trigger")}
                </Button>
                <Button
                  color="secondary"
                  disableElevation
                  type="button"
                  variant="outlined"
                  onClick={handleCancel}
                >
                  {t("common.buttons.cancel")}
                </Button>
              </Box>
            </Form>
          )
        }}
      </Formik>
    </>
  );
};

export default TriggerPostback;
